import React, { useRef, useEffect } from 'react';

const STROKE_WIDTH = 18;

export default function DimensionalBrush() {
  const canvasRef = useRef(null);
  const lastPos = useRef(null);
  const lastMid = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      canvas.getContext('2d').setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const draw = (e) => {
      const ctx = canvas.getContext('2d');
      const pos = { x: e.clientX, y: e.clientY };

      if (!lastPos.current) { lastPos.current = pos; return; }

      const x1 = lastPos.current.x, y1 = lastPos.current.y;
      const x2 = pos.x, y2 = pos.y;
      const midX = (x1 + x2) / 2, midY = (y1 + y2) / 2;

      if (!lastMid.current) { lastMid.current = { x: midX, y: midY }; lastPos.current = pos; return; }

      const fromX = lastMid.current.x, fromY = lastMid.current.y;
      const sdx = midX - fromX, sdy = midY - fromY;
      const slen = Math.sqrt(sdx * sdx + sdy * sdy) || 1;
      const r = STROKE_WIDTH / 2;
      const px = -sdy / slen, py = sdx / slen;
      const litSide = ((-0.6) * px + (-0.8) * py) >= 0 ? 1 : -1;
      const cmx = (fromX + midX) / 2, cmy = (fromY + midY) / 2;
      const gx1 = cmx + px * r * litSide, gy1 = cmy + py * r * litSide;
      const gx2 = cmx - px * r * litSide, gy2 = cmy - py * r * litSide;

      const grad = ctx.createLinearGradient(gx2, gy2, gx1, gy1);
      grad.addColorStop(0,    'rgb(0,0,0)');
      grad.addColorStop(0.25, 'rgb(100,100,100)');
      grad.addColorStop(0.55, 'rgb(160,160,160)');
      grad.addColorStop(0.75, 'rgb(210,210,210)');
      grad.addColorStop(0.88, 'rgb(245,245,245)');
      grad.addColorStop(0.95, 'rgb(200,200,200)');
      grad.addColorStop(1,    'rgb(30,30,30)');

      ctx.beginPath();
      ctx.moveTo(fromX, fromY);
      ctx.quadraticCurveTo(x1, y1, midX, midY);
      ctx.strokeStyle = grad;
      ctx.lineWidth = STROKE_WIDTH;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();

      lastMid.current = { x: midX, y: midY };
      lastPos.current = pos;
    };

    const reset = () => { lastPos.current = null; lastMid.current = null; };

    let rafId;
    const fade = () => {
      const ctx = canvas.getContext('2d');
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = 'rgba(0, 0, 0, 0.03)';
      ctx.fillRect(0, 0, canvas.width / dpr, canvas.height / dpr);
      ctx.restore();
      rafId = requestAnimationFrame(fade);
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        cancelAnimationFrame(rafId);
        rafId = null;
      } else {
        if (!rafId) rafId = requestAnimationFrame(fade);
      }
    };

    rafId = requestAnimationFrame(fade);
    document.addEventListener('visibilitychange', onVisibilityChange);

    window.addEventListener('mousemove', draw, { passive: true });
    window.addEventListener('mouseleave', reset);
    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', draw);
      window.removeEventListener('mouseleave', reset);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'fixed', top: 0, left: 0, zIndex: 100, pointerEvents: 'none' }}
    />
  );
}
